import { useAuth, useClerk } from '@clerk/clerk-react'
import { captureMessage } from '@sentry/react'
import { usePostHog } from 'posthog-js/react'
import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuthTokenOverride } from 'app/contexts/AuthTokenContext'
import { isUnsignedAuthMode } from 'app/e2e/mode'
import { FUNNEL_EVENTS, FUNNEL_EVENT_PROPERTY_KEYS, OTP_FAILURE_REASONS, type OtpFailureReason } from 'app/tracking/events'
import { classifyClerkOtpAttemptFailure } from 'app/tracking/mapOtpAttemptFailure'
import { OFFER_STATUS, PERSONAL_LOAN_ROUTES } from 'app/utils/constants'

type PostHogClient = ReturnType<typeof usePostHog>

function captureOtpSent(posthog: PostHogClient, applicationId: string | undefined, isResend: boolean): void {
    if (!posthog || !applicationId) {
        return
    }

    try {
        posthog.capture(FUNNEL_EVENTS.OTP_SENT, {
            [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: applicationId,
            ...(isResend ? { [FUNNEL_EVENT_PROPERTY_KEYS.IS_RESEND]: true } : {})
        })
    } catch (error) {
        captureMessage('Failed to capture otp_sent', {
            level: 'error',
            extra: { error }
        })
    }
}

function captureOtpVerified(posthog: PostHogClient, applicationId: string | undefined): void {
    if (!posthog || !applicationId) {
        return
    }

    try {
        posthog.capture(FUNNEL_EVENTS.OTP_VERIFIED, {
            [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: applicationId
        })
    } catch (error) {
        captureMessage('Failed to capture otp_verified', {
            level: 'error',
            extra: { error }
        })
    }
}

function captureOtpFailed(
    posthog: PostHogClient,
    applicationId: string | undefined,
    reason: OtpFailureReason,
    options?: { isResend?: boolean; errorCode?: string }
): void {
    if (!posthog || !applicationId) {
        return
    }

    try {
        posthog.capture(FUNNEL_EVENTS.OTP_FAILED, {
            [FUNNEL_EVENT_PROPERTY_KEYS.APPLICATION_ID]: applicationId,
            [FUNNEL_EVENT_PROPERTY_KEYS.REASON]: reason,
            ...(options?.isResend ? { [FUNNEL_EVENT_PROPERTY_KEYS.IS_RESEND]: true } : {}),
            ...(options?.errorCode ? { [FUNNEL_EVENT_PROPERTY_KEYS.ERROR_CODE]: options.errorCode } : {})
        })
    } catch (error) {
        captureMessage('Failed to capture otp_failed', {
            level: 'error',
            extra: { error }
        })
    }
}

// Global OTP state management to prevent duplicates across hook instances
const GLOBAL_OTP_STATE_PREFIX = 'figOtpState'
const EMAIL_MANUALLY_UPDATED_PREFIX = 'figEmailManuallyUpdated'

interface GlobalOTPState {
    otpSent: boolean
    lastEmailSent: string
    timestamp: number
}

const resetGlobalOTPState = (applicationId: string) => {
    try {
        sessionStorage.removeItem(`${GLOBAL_OTP_STATE_PREFIX}_${applicationId}`)
    } catch {
        // Ignore storage errors
    }
}

const resetEmailManuallyUpdated = (applicationId: string) => {
    try {
        sessionStorage.removeItem(`${EMAIL_MANUALLY_UPDATED_PREFIX}_${applicationId}`)
    } catch {
        // Ignore storage errors
    }
}

const getGlobalOTPState = (applicationId: string): GlobalOTPState => {
    try {
        const stored = sessionStorage.getItem(`${GLOBAL_OTP_STATE_PREFIX}_${applicationId}`)
        if (stored) {
            const state = JSON.parse(stored) as GlobalOTPState
            // Reset if more than 5 minutes old
            if (Date.now() - state.timestamp > 5 * 60 * 1000) {
                resetGlobalOTPState(applicationId)
                return { otpSent: false, lastEmailSent: '', timestamp: Date.now() }
            }
            return state
        }
    } catch {
        // Ignore errors, fall back to default
    }
    return { otpSent: false, lastEmailSent: '', timestamp: Date.now() }
}

const setGlobalOTPState = (applicationId: string, state: Partial<GlobalOTPState>) => {
    try {
        const current = getGlobalOTPState(applicationId)
        const newState = { ...current, ...state, timestamp: Date.now() }
        sessionStorage.setItem(`${GLOBAL_OTP_STATE_PREFIX}_${applicationId}`, JSON.stringify(newState))
    } catch {
        // Ignore storage errors
    }
}

const getEmailManuallyUpdated = (applicationId: string): boolean => {
    try {
        const stored = sessionStorage.getItem(`${EMAIL_MANUALLY_UPDATED_PREFIX}_${applicationId}`)
        if (stored) {
            const data = JSON.parse(stored)
            // Reset if more than 10 minutes old
            if (Date.now() - data.timestamp > 10 * 60 * 1000) {
                resetEmailManuallyUpdated(applicationId)
                return false
            }
            return data.isManuallyUpdated === true
        }
    } catch {
        // Ignore errors
    }
    return false
}

const setEmailManuallyUpdated = (applicationId: string, isManuallyUpdated: boolean) => {
    try {
        const data = { isManuallyUpdated, timestamp: Date.now() }
        sessionStorage.setItem(`${EMAIL_MANUALLY_UPDATED_PREFIX}_${applicationId}`, JSON.stringify(data))
    } catch {
        // Ignore storage errors
    }
}

interface UseOTPOptions {
    // Offer page specific options
    offer?: any
    applicationId?: string
    navigate?: (path: string, options?: { replace?: boolean }) => void
    isEmailManuallyUpdated?: boolean
}

/**
 * Comprehensive hook for handling OTP (One-Time Password) authentication using Clerk.
 * Handles both basic OTP operations and offer page specific logic including email validation.
 *
 * @param options - Optional configuration for offer page behavior
 * @returns Object containing OTP handler functions and offer page utilities
 */
const useOTP = (options?: UseOTPOptions) => {
    // ========================================
    // SETUP & STATE
    // ========================================
    const clerk = useClerk()
    const { isSignedIn, isLoaded } = useAuth()
    const posthog = usePostHog()
    const authTokenOverride = useAuthTokenOverride()
    const unsignedAuthMode = isUnsignedAuthMode(authTokenOverride)

    // Core OTP state
    const preparingRef = useRef(false)
    const lastEmailRef = useRef<string>('')

    // Offer page specific state
    const [clerkReady, setClerkReady] = useState(false)
    const [isValidating, setIsValidating] = useState(false)

    // Extract options with defaults
    const { offer, applicationId, navigate, isEmailManuallyUpdated = false } = options || {}

    // Handle both direct response and data-wrapped response structures
    const offerData = offer?.data || offer
    const isDeclined = offerData?.status === OFFER_STATUS.DECLINED
    const isSuccess = offerData?.status === OFFER_STATUS.PRE_AUTHORIZED || offerData?.status === OFFER_STATUS.PENDING

    // ========================================
    // CORE OTP FUNCTIONS
    // ========================================

    /**
     * Checks if there's already a pending sign-in attempt for the given email.
     */
    const hasPendingSignIn = useCallback(
        (email: string) => {
            if (unsignedAuthMode) {
                return false
            }

            const existingSignIn = clerk.client?.signIn
            return (
                existingSignIn &&
                (existingSignIn.status === 'needs_first_factor' || existingSignIn.status === 'needs_second_factor') &&
                existingSignIn.identifier === email
            )
        },
        [clerk, unsignedAuthMode]
    )

    /**
     * Prepares Clerk to send an email code for sign-in to the specified email address.
     * First checks if there's already a pending sign-in attempt to avoid sending duplicate emails.
     */
    const handleClerkSignInPreparation = useCallback(
        async (email: string, isResend: boolean = false) => {
            if (preparingRef.current) {
                return // Prevent multiple calls
            }
            if (!isResend && lastEmailRef.current === email) {
                return // Prevent duplicate emails unless it's a resend
            }

            if (unsignedAuthMode) {
                return
            }

            preparingRef.current = true
            lastEmailRef.current = email

            let emailWasSent = false

            try {
                // Check if there's already a pending sign-in attempt for this email
                const existingSignIn = clerk.client.signIn
                const isExistingSignInValid =
                    existingSignIn &&
                    (existingSignIn.status === 'needs_first_factor' || existingSignIn.status === 'needs_second_factor') &&
                    existingSignIn.identifier === email

                let supportedFirstFactors

                if (isExistingSignInValid && !isResend) {
                    // Reuse existing sign-in attempt - no new email will be sent
                    supportedFirstFactors = existingSignIn.supportedFirstFactors
                } else {
                    // Create new sign-in attempt or handle resend - this will send an email
                    const signInResult = await clerk.client.signIn.create({
                        strategy: 'email_code',
                        identifier: email
                    })
                    supportedFirstFactors = signInResult.supportedFirstFactors
                    emailWasSent = true
                }

                const firstEmailFactor = supportedFirstFactors?.find((factor) => {
                    return factor.strategy === 'email_code'
                })
                if (!firstEmailFactor) {
                    throw new Error('Email verification factor not found')
                }
                const { emailAddressId } = firstEmailFactor

                // IMPORTANT: We no longer call prepareFirstFactor because signIn.create already sends the email
                // Only use prepareFirstFactor when we have an existing sign-in but need to prepare the factor
                const needsPreparation = isExistingSignInValid && !existingSignIn.supportedFirstFactors?.some((f) => f.strategy === 'email_code')

                if (needsPreparation) {
                    await clerk.client.signIn.prepareFirstFactor({
                        strategy: 'email_code',
                        emailAddressId
                    })
                    emailWasSent = true
                }
            } catch (error) {
                if (applicationId) {
                    captureOtpFailed(posthog, applicationId, isResend ? OTP_FAILURE_REASONS.RESEND_FAILED : OTP_FAILURE_REASONS.SEND_FAILED, {
                        isResend
                    })
                }
                captureMessage('Failed to prepare Clerk sign-in', {
                    level: 'error',
                    extra: { error }
                })
                throw error // Re-throw so the calling code knows it failed
            } finally {
                preparingRef.current = false
                // Clear the last email after 30 seconds to allow legitimate resends
                setTimeout(() => {
                    lastEmailRef.current = ''
                }, 30000)
            }

            if (emailWasSent && applicationId) {
                captureOtpSent(posthog, applicationId, isResend)
            }
        },
        [clerk, unsignedAuthMode, applicationId, posthog]
    )

    /**
     * Attempts to sign in using the provided verification code.
     */
    const handleClerkSignInAttempt = useCallback(
        async (verificationCode: string) => {
            if (unsignedAuthMode) {
                return { isSuccess: verificationCode.length === 6, error: '' }
            }

            try {
                const verify = await clerk.client.signIn.attemptFirstFactor({
                    strategy: 'email_code',
                    code: verificationCode
                })
                await clerk.setActive({ session: verify.createdSessionId })
            } catch (error) {
                const { reason, errorCode } = classifyClerkOtpAttemptFailure(error)
                if (applicationId) {
                    captureOtpFailed(posthog, applicationId, reason, { errorCode })
                }
                captureMessage('Failed to attempt Clerk sign-in', {
                    level: 'error',
                    extra: { error, reason, errorCode }
                })
                return { isSuccess: false, error: 'Invalid verification code' }
            }

            if (applicationId) {
                captureOtpVerified(posthog, applicationId)
            }
            return { isSuccess: true, error: '' }
        },
        [clerk, unsignedAuthMode, applicationId, posthog]
    )

    // ========================================
    // EMAIL VALIDATION
    // ========================================

    /**
     * Validate that the currently signed-in user's email matches the application's email.
     * If there's a mismatch, this will clear the Clerk session to force re-authentication.
     * Skip validation if email was manually updated to prevent logout after successful email change.
     */
    const validateEmailAndClearSession = useCallback(async () => {
        if (!isLoaded || !isSignedIn || !offer || !applicationId) {
            return
        }

        // Check if email was manually updated (either from prop or global state)
        const globalEmailManuallyUpdated = applicationId ? getEmailManuallyUpdated(applicationId) : false
        if (isEmailManuallyUpdated || globalEmailManuallyUpdated) {
            return
        }

        // Extract email from offer data - handle all three types
        let offerEmail: string | undefined

        if ('data' in offer) {
            // PrequalificationAffiliateResponse
            offerEmail = (offer.data as any)?.borrower_data?.email
        } else {
            // OfferDataResponseApiType or PersonalLoansV2ApiOfferGetOfferData200
            offerEmail = (offer as any)?.borrower_data?.email
        }

        if (!offerEmail) {
            captureMessage('No email found in offer data', {
                level: 'warning',
                extra: {
                    applicationId,
                    hasWrappedOfferData: 'data' in offer
                }
            })
            return
        }

        // Get the current user's email from Clerk
        const currentUserEmail = clerk.user?.primaryEmailAddress?.emailAddress

        if (!currentUserEmail) {
            captureMessage('No primary email found for current Clerk user', {
                level: 'warning',
                extra: {
                    applicationId,
                    hasClerkUser: Boolean(clerk.user)
                }
            })
            return
        }

        // Check if emails match (case-insensitive)
        const emailsMatch = offerEmail.toLowerCase() === currentUserEmail.toLowerCase()

        if (!emailsMatch) {
            setIsValidating(true) // Show loading state during sign out

            try {
                // Clear the current Clerk session
                await clerk.signOut({ redirectUrl: window.location.href })
            } catch (error) {
                captureMessage('Failed to clear Clerk session', {
                    level: 'error',
                    extra: {
                        error,
                        applicationId,
                        hasOfferEmail: Boolean(offerEmail),
                        hasCurrentUserEmail: Boolean(currentUserEmail)
                    }
                })
                setIsValidating(false) // Reset loading state on error
            }
        }
    }, [isLoaded, isSignedIn, offer, applicationId, clerk, isEmailManuallyUpdated])

    // ========================================
    // OFFER PAGE UTILITIES
    // ========================================

    /**
     * Reset OTP sent status for email changes
     */
    const resetOTPStatus = useCallback(() => {
        if (applicationId) {
            resetGlobalOTPState(applicationId)
        }
    }, [applicationId])

    /**
     * Mark OTP as sent
     */
    const markOTPAsSent = useCallback(
        (email?: string) => {
            if (applicationId) {
                setGlobalOTPState(applicationId, { otpSent: true, lastEmailSent: email || '' })
            }
        },
        [applicationId]
    )

    /**
     * Handle email change with OTP preparation
     */
    const handleEmailChangeWithOTP = useCallback(
        async (newEmail: string) => {
            resetOTPStatus()
            if (clerkReady && !isSignedIn) {
                try {
                    await handleClerkSignInPreparation(newEmail)
                    markOTPAsSent(newEmail)
                } catch (err) {
                    resetOTPStatus()
                    captureMessage('Error during Clerk initialization with new email', {
                        level: 'error',
                        extra: {
                            error: err,
                            applicationId
                        }
                    })
                    throw err // Re-throw to let calling component handle it
                }
            }
        },
        [clerkReady, isSignedIn, handleClerkSignInPreparation, applicationId, resetOTPStatus, markOTPAsSent]
    )

    /**
     * Handle manual OTP resend
     */
    const handleResendOTP = useCallback(
        async (email: string) => {
            if (clerkReady) {
                try {
                    await handleClerkSignInPreparation(email, true) // Force resend
                    markOTPAsSent(email)
                } catch (err) {
                    captureMessage('Error during manual OTP resend', {
                        level: 'error',
                        extra: {
                            error: err,
                            applicationId,
                            email
                        }
                    })
                    throw err // Re-throw to let calling component handle it
                }
            }
        },
        [clerkReady, handleClerkSignInPreparation, applicationId, markOTPAsSent]
    )

    // ========================================
    // EFFECTS
    // ========================================

    // Monitor when Clerk client is ready
    useEffect(() => {
        if (clerk.client || unsignedAuthMode) {
            setClerkReady(true)
        }
    }, [clerk.client, unsignedAuthMode])

    // Handle automatic OTP sending for offer pages
    useEffect(() => {
        if (!options || !navigate) return // Skip if not used for offer pages

        const handleAutomaticOTP = async () => {
            if (!applicationId) return

            if (offerData) {
                if (isDeclined) {
                    navigate(`/${PERSONAL_LOAN_ROUTES.ROOT}/${applicationId}/${PERSONAL_LOAN_ROUTES.DECLINED}`, {
                        replace: true
                    })
                    return
                }

                // Check global OTP state to prevent duplicates across all hook instances
                const globalState = getGlobalOTPState(applicationId)

                if (isSuccess && offerData && clerkReady && !isSignedIn && !isEmailManuallyUpdated && !globalState.otpSent) {
                    const userEmail = offerData.borrower_data?.email ?? ''

                    if (!userEmail) {
                        captureMessage('No email found in offer data for OTP sending', {
                            level: 'warning',
                            extra: { applicationId, offerStatus: offerData.status }
                        })
                        return
                    }

                    // Additional check: prevent sending to the same email twice (global check)
                    if (globalState.lastEmailSent === userEmail) {
                        setGlobalOTPState(applicationId, { otpSent: true })
                        return
                    }

                    // Only send OTP if there's no pending sign-in attempt
                    if (!hasPendingSignIn(userEmail)) {
                        setGlobalOTPState(applicationId, { otpSent: true, lastEmailSent: userEmail }) // Mark as sent globally
                        try {
                            await handleClerkSignInPreparation(userEmail)
                        } catch (err) {
                            resetGlobalOTPState(applicationId) // Reset global state on error
                            captureMessage('Error during Clerk initialization', {
                                level: 'error',
                                extra: {
                                    error: err,
                                    applicationId
                                }
                            })
                        }
                    } else {
                        // There's already a pending sign-in, mark as sent to avoid future attempts
                        setGlobalOTPState(applicationId, { otpSent: true, lastEmailSent: userEmail })
                    }
                }
            }
        }

        handleAutomaticOTP()
        // Intentionally excluding hasPendingSignIn and handleClerkSignInPreparation from deps
        // to prevent unnecessary re-runs that cause duplicate OTP sends
    }, [offerData, isDeclined, isSuccess, clerkReady, isSignedIn, isEmailManuallyUpdated, applicationId, navigate])

    // Run email validation whenever the dependencies change (only for offer pages)
    useEffect(() => {
        if (options && offer && applicationId) {
            validateEmailAndClearSession()
        }
    }, [validateEmailAndClearSession, options, offer, applicationId])

    // ========================================
    // RETURN API
    // ========================================

    return {
        // Core OTP functions (always available)
        handleClerkSignInAttempt,
        handleClerkSignInPreparation,
        hasPendingSignIn,

        // Offer page specific functions (only when options provided)
        ...(options && {
            // Data & State
            offerData,
            isDeclined,
            isSuccess,
            clerkReady,
            isValidating,

            // OTP Management
            resetOTPStatus,
            markOTPAsSent,
            handleEmailChangeWithOTP,
            handleResendOTP,

            // Email Validation
            validateEmailAndClearSession,

            // Global state management
            setEmailManuallyUpdated: (isManuallyUpdated: boolean) => (applicationId ? setEmailManuallyUpdated(applicationId, isManuallyUpdated) : undefined),
            resetEmailManuallyUpdated: () => (applicationId ? resetEmailManuallyUpdated(applicationId) : undefined),
            isEmailChangeDisabled: () => (applicationId ? getEmailManuallyUpdated(applicationId) : false)
        })
    }
}

export default useOTP
